import CleaningServicesOutlinedIcon from "@mui/icons-material/CleaningServicesOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Users } from "../../api/account/Users";
import { KeenAPI } from "../../api/KeenAPI";
import Tabulator, {
  EventsType,
  reactFormatter,
} from "../../components/Tabulator";
import { deepClone } from "../../deepClone";
import { useAuth } from "../../hooks/useAuth";
import { TDate } from "../../trendrating/date/TDate";
import { modalStyle } from "../DailyReport/utils";
import { useFeedbackSystem } from "../../hooks/useFeedbackSystem";

export default function UserManager() {
  const userAPI = useMemo(() => new Users(), []);
  const [rows, setRows] = useState<any>([]);
  const feedback = useFeedbackSystem();
  const auth = useAuth();
  const _KeenAPI = useMemo(() => {
    return new KeenAPI(auth.user.additionalCookies);
  }, [auth.user.additionalCookies]);
  const users = useMemo(() => {
    if (rows.length > 0) {
      const _users: any = {};
      rows.forEach((element) => {
        _users[element.id] = {
          firstName: element.firstName,
          lastName: element.lastName,
        };
      });
      return _users;
    }
    return [];
  }, [rows]);

  const getLastAction = useCallback(
    (ids) => {
      const keenRequestObj = {
        operationType: "queries",
        params: {
          analysis_type: "maximum",
          target_property: "keen.timestamp",
          timezone: "Etc/UTC",
          group_by: ["user_id"],
          timeframe: "this_1_years",
          filters: [
            {
              operator: "in",
              property_type: "Number",
              property_value: ids,
              property_name: "user_id",
            },
          ],
        },
      };
      return _KeenAPI.proxy(keenRequestObj);
    },
    [_KeenAPI]
  );

  const [isLoading, setIsLoading] = useState(false);
  const getTableData = useCallback(() => {
    setIsLoading(true);

    userAPI
      .getByFilter({ active: false })
      .then((response) => {
        return userAPI.get({
          ids: [...response],
          properties: [
            "firstName",
            "lastName",
            "email",
            "group",
            "level",
            "creationTime",
            "product",
            "active",
            "level",
            "product",
            "owner",
          ],
        });
      })
      .then((res) => {
        let _rows = deepClone(res.data[0].rows);
        _rows.forEach((row) => {
          row["productCodeName"] = row.product.codeName;
        });
        getLastAction(res.data[0].rows.map((item) => item.id)).then(
          (_res) => {
            const data = _res.result;
            _rows.forEach((row) => {
              data.forEach((element) => {
                if (row.id === parseInt(element.user_id)) {
                  row["lastAccess"] = element.result;
                }
              });
            });
            setRows(_rows);
            setIsLoading(false);
          },
          (error) => {
            _rows.forEach((row) => {
              row["lastAccess"] = "-";
            });
            setRows(_rows);
            setIsLoading(false);
          }
        );
      });
  }, [getLastAction, userAPI]);

  useEffect(() => {
    getTableData();
  }, [getTableData]);

  const [deleteReport, setDeleteReport] = useState<any>(null);
  const _delete = useCallback(
    (id) => {
      userAPI.deleteUser(id, false, true).then((res) => {
        if (res.status === "OK") {
          feedback.show({
            type: "success",
            message: `Successfully deleted ${users[id].firstName} ${users[id].lastName}! &#128512;`,
          });
          const table = tabulatorRef.current.getTableInstance();
          const data = table.getData();
          const datum = table.getRow(id).getData();
          const filteredData = data.filter((item) => item.id !== datum.id);
          table.replaceData(filteredData);
        } else {
          feedback.show({
            type: "error",
            message: `Could not delete ${users[id].firstName} ${users[id].lastName}! &#128517;`,
          });
        }
      });
    },
    [feedback, userAPI, users]
  );
  const clearHandler = useCallback(
    (id) => {
      userAPI.deleteUser(id, true, false).then((res) => {
        if (res.status === "OK") {
          feedback.show({
            type: "success",
            message: `Successfully cleaned ${users[id].firstName} ${users[id].lastName}! &#128512;`,
          });
        } else {
          feedback.show({
            type: "error",
            message: `Could not clean ${users[id].firstName} ${users[id].lastName}! &#128517;`,
          });
        }
      });
    },
    [feedback, userAPI, users]
  );
  const _columns: any[] = useMemo(
    () => [
      {
        title: "ID",
        field: "id",
        sorter: "number",
        widthGrow: 0.5,
        hozAlign: "center",
        formatter:(cell)=>{
          const value = cell.getValue();
          return `<strong>${value}</strong>`
        }
      },
      {
        title: "Client",
        field: "group",
        sorter: "string",
        hozAlign: "left",
        widthGrow: 2,
      },
      {
        title: "First name",
        field: "firstName",
        sorter: "string",
        hozAlign: "left",
        widthGrow: 1,
      },
      {
        title: "Last name",
        field: "lastName",
        sorter: "string",
        hozAlign: "left",
        widthGrow: 1,
      },
      {
        title: "Email",
        field: "email",
        sorter: "string",
        hozAlign: "left",
        widthGrow: 2,
      },
      {
        title: "Product",
        field: "productCodeName",
        sorter: "string",
        hozAlign: "left",
      },
      { title: "Owner", field: "owner", sorter: "string", hozAlign: "left" },
      {
        title: "Type",
        field: "level",
        sorter: "string",
        hozAlign: "left",

        widthGrow: 0.7,
        formatter: (cell) => {
          return cell.getValue().toLowerCase();
        },
      },
      {
        title: "Created at",
        field: "creationTime",
        sorter: "number",
        formatter: (cell) => {
          const value = cell.getValue();
          const date = TDate.dateToIso8601(new Date(value));
          return date;
        },
      },
      {
        title: "Last access",
        field: "lastAccess",
        formatter: (cell) => {
          const value = cell.getValue();
          if (value) {
            const _date = new Date(value);
            if (_date instanceof Date && !isNaN(_date.valueOf())) {
              const date = TDate.dateToIso8601(new Date(value));
              return date;
            }
            return "-";
          }
          return "More than a year ago";
        },
      },
      {
        title: "Active",
        field: "active",
        widthGrow: 0.3,
        formatter: function (cell) {
          let value = cell.getValue();
          return `<div style="background-color: ${
            value ? "green" : "red"
          };height: 0.5vw;width: 0.5vw;border-radius: 100px"></div>`;
        },
        hozAlign: "center",
        vertAlign: "center",
      },
      {
        headerSort: false,
        title: "Clean",
        field: "clean",
        widthGrow: 0.5,
        hozAlign: "center",

        cellClick: (e, cell) => {
          setShowClearModal(true);
          setIdToClear(cell.getData().id);
        },
        formatter: reactFormatter(
          <CleaningServicesOutlinedIcon sx={{ fontSize: "0.9vw" }} />
        ),
      },

      {
        headerSort: false,
        title: "Delete",
        field: "delete",
        hozAlign: "center",
        widthGrow: 0.5,
        cellClick: (e, cell) => {
          setShowDeleteModal(true);
          setIdToDelete(cell.getData().id);
        },
        formatter: reactFormatter(<DeleteIcon sx={{ fontSize: "0.9vw" }} />),
      },
    ],
    []
  );
  const tableOptions = useMemo(
    () => ({
      layout: "fitColumns",
    }),
    []
  );
  const tabulatorRef = useRef<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [idToDelete, setIdToDelete] = useState<any>(null);
  useEffect(() => {
    if (showDeleteModal && idToDelete) {
      userAPI.deleteUser(idToDelete, true, true).then((response) => {
        let hasStuff = false;
        let lis: any[] = [];
        const keys = Object.keys(response.data);
        for (let i = 0; i < keys.length; i++) {
          const len = response.data[keys[i]].length;
          if (len > 0) {
            hasStuff = true;
            lis.push(
              <li>
                <Typography>
                  {keys[i]}: {len}
                </Typography>
              </li>
            );
            // break;
          }
        }
        if (hasStuff) {
          setDeleteReport(
            <Box display={"flex"} flexDirection={"column"}>
              <Typography>
                Are you sure you want to delete{" "}
                <strong>
                  {users[idToDelete].firstName} {users[idToDelete].lastName} (ID{" "}
                  {idToDelete})
                </strong>{" "}
                ?<br />
                the user still has:
              </Typography>
              <ul>{lis.map((item) => item)}</ul>
              <Box
                width={"100%"}
                display={"flex"}
                justifyContent={"flex-end"}
                gap={2}
              >
                <Button
                  onClick={() => {
                    setDeleteReport(null);
                    _delete(idToDelete);
                    setIdToDelete(null);
                    setShowDeleteModal(false);
                  }}
                  color="secondary"
                >
                  Delete
                </Button>
                <Button
                  onClick={() => {
                    setDeleteReport(null);
                    setIdToDelete(null);
                    setShowDeleteModal(false);
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          );
        } else {
          setDeleteReport(
            <Box display={"flex"} flexDirection={"column"}>
              <Typography>
                Are you sure you want to delete{" "}
                <strong>
                  {users[idToDelete].firstName} {users[idToDelete].lastName} (ID{" "}
                  {idToDelete})
                </strong>{" "}
                ?
              </Typography>
              <Box
                width={"100%"}
                display={"flex"}
                justifyContent={"flex-end"}
                gap={2}
              >
                <Button
                  onClick={() => {
                    setDeleteReport(null);
                    _delete(idToDelete);
                  }}
                  color="secondary"
                >
                  Delete
                </Button>
                <Button onClick={() => setDeleteReport(null)}>Cancel</Button>
              </Box>
            </Box>
          );
        }
      });
    }
  }, [_delete, idToDelete, showDeleteModal, userAPI, users]);
  const deleteModal = useMemo(
    () => (
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Box sx={modalStyle}>{deleteReport}</Box>
      </Modal>
    ),
    [deleteReport, showDeleteModal]
  );

  const [showClearModal, setShowClearModal] = useState(false);
  const [idToClear, setIdToClear] = useState<any>(null);
  const clearModal = useMemo(
    () => (
      <Modal open={showClearModal} onClose={() => setShowClearModal(false)}>
        <Box sx={modalStyle}>
          <Typography>
            Are you sure you want to clear{" "}
            <strong>
              {users[idToClear]?.firstName} {users[idToClear]?.lastName} (ID:
              {idToClear})
            </strong>
          </Typography>
          <Box display={"flex"} gap={1} width={"100%"} justifyContent={"flex-end"}>
            <Button
              color="secondary"
              onClick={() => {
                clearHandler(idToClear);
                setIdToClear(null);
                setShowClearModal(false);
              }}
            >
              Clear
            </Button>
            <Button
              onClick={() => {
                setIdToClear(null);
                setShowClearModal(false);
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    ),
    [clearHandler, idToClear, showClearModal, users]
  );

  const [tableReady, setTableReady] = useState(false);

  useEffect(() => {
    if (tableReady) {
      if (tabulatorRef.current && rows.length > 0) {
        const table = tabulatorRef.current.getTableInstance();
        // let data = table.getData();
        if (/*data.length <= 0 &&*/ table.initialized) {
          table.setData(rows).then(() => {
            table.redraw();
          });
        }
      }
    }
  }, [rows, tableReady]);


  useEffect(() => {
    if (tableReady) {
      if (tabulatorRef.current && _columns) {
        const table = tabulatorRef.current.getTableInstance();
        table.initialized && table.setColumns(_columns);
      }
    }
  }, [_columns, tableReady]);

  const events: EventsType = useMemo(
    () => [
      {
        event: "tableBuilt",
        callback: () => setTableReady(true),
      },
      {
        event: "tableDestroyed",
        callback: () => setTableReady(false),
      },
    ],
    []
  );
  const tableComponent = useMemo(
    () => (
      <Tabulator events={events} ref={tabulatorRef} options={tableOptions} />
    ),
    [events, tableOptions]
  );
  return (
    <Box
      height={"100%"}
      width={"100%"}
      sx={{ boxShadow: 3 }}
      // borderRadius={"4px"}
      gap={2}
      display={"flex"}
      flexDirection={"column"}
      position={"relative"}
    >
      {isLoading && (
        <Box
          bgcolor={"white"}
          display={"flex"}
          gap={2}
          zIndex={3}
          position={"absolute"}
          top={0}
          left={0}
          width={"100%"}
          height={"100%"}
          flexDirection={"column"}
          alignItems={"center"}
          justifyContent={"center"}
        >
          <Typography>Loading Users data...</Typography>
          <CircularProgress color="secondary" />
        </Box>
      )}
      {deleteModal}
      {clearModal}
      {tableComponent}
    </Box>
  );
}
